import React, { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { ProProvider } from "./contexts/ProContext";
import { ReadingProgressProvider } from "./contexts/ReadingProgressContext";
import { FortressProvider, useFortress } from "./contexts/FortressContext";
import { PrayerScheduleProvider } from "./contexts/PrayerScheduleContext";
import { Toaster } from "./components/ui/sonner";
import { setupWidgetDeepLinkHandler } from "./services/widgetService";
import { bootInitAdhan } from "./services/adhanNotificationService";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { useAnalytics } from "./hooks/useAnalytics";

// Critical Pages - Load immediately for fast First Contentful Paint
import HomePage from "./pages/HomePage";
import QuranPage from "./pages/QuranPage";
import PrayerTrialPage from "./pages/PrayerTrialPage";

// Non-critical Pages - Lazy loaded for better performance
const SurahPage = lazy(() => import("./pages/SurahPage"));
const ZoomViewPage = lazy(() => import("./pages/ZoomViewPage"));
const MushafViewPage = lazy(() => import("./pages/MushafViewPage"));
const PrayerTimes = lazy(() => import("./pages/PrayerTimes"));
const HadithPage = lazy(() => import("./pages/HadithPage"));
const QiblaPage = lazy(() => import("./pages/QiblaPage"));
const MasjidGuidePage = lazy(() => import("./pages/MasjidGuidePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const BookmarksPage = lazy(() => import("./pages/BookmarksPage"));
const DuasPage = lazy(() => import("./pages/DuasPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const ReadingProgressPage = lazy(() => import("./pages/ReadingProgressPage"));
const HalalScannerPage = lazy(() => import("./pages/HalalScannerPage"));
const HalalScanner2Page = lazy(() => import("./pages/HalalScanner2Page"));
const DailyStoriesPage = lazy(() => import("./pages/DailyStoriesPage"));
const LearnPage = lazy(() => import("./pages/LearnPage"));
const KidsProgressPage = lazy(() => import("./pages/KidsProgressPage"));
const AdminFortressPage = lazy(() => import("./pages/AdminFortressPage"));
const KidsParadisePage = lazy(() => import("./pages/KidsParadisePage"));
// V3 PARALLEL TRACK: Local AI Quran Reader (Clean architecture)
const QuranPageV3 = lazy(() => import("./pages/QuranPageV3"));
const SurahPageV3 = lazy(() => import("./pages/SurahPageV3"));
// Web Subscription (Stripe checkout)
const WebSubscriptionPage = lazy(() => import("./pages/WebSubscriptionPage"));
// Privacy Policy
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
// Delete Account
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));

// Components
import Layout from "./components/Layout";
import WelcomeModal from "./components/WelcomeModal";

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
  </div>
);

// 🚀 Hide loading skeleton when React mounts
const hideLoadingSkeleton = () => {
  const skeleton = document.getElementById('app-skeleton');
  if (skeleton && !skeleton.classList.contains('hidden')) {
    document.body.classList.add('loaded');
    skeleton.classList.add('hidden');
    setTimeout(() => {
      skeleton.style.display = 'none';
    }, 400);
  }
};

// 🔗 Widget Deep Link Handler - Routes widget taps to correct pages
function WidgetDeepLinkHandler() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const cleanup = setupWidgetDeepLinkHandler(navigate);
    return cleanup;
  }, [navigate]);
  
  return null; // This component doesn't render anything
}

// 📊 FORTRESS: Visitor Analytics Tracker - Tracks page views and sessions
function AnalyticsTracker() {
  useAnalytics();
  return null;
}

// 🏰 FORTRESS: Breadcrumb Tracker - Tracks navigation for audit trails
function FortressBreadcrumbTracker() {
  const location = useLocation();
  const { trackBreadcrumb, isLoaded } = useFortress();
  const previousPath = React.useRef(null);
  
  useEffect(() => {
    if (!isLoaded) return;
    
    const currentPath = location.pathname;
    if (currentPath !== previousPath.current) {
      trackBreadcrumb(currentPath, previousPath.current);
      previousPath.current = currentPath;
    }
  }, [location.pathname, trackBreadcrumb, isLoaded]);
  
  return null;
}

// 🏰 FORTRESS: Maintenance Mode Overlay
function MaintenanceModeOverlay() {
  const { maintenanceMode, isLoaded } = useFortress();
  
  if (!isLoaded || !maintenanceMode) return null;
  
  return (
    <div className="fixed inset-0 z-[99999] bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-amber-500" />
        </div>
        <h2 className="text-xl font-bold mb-2">Maintenance Mode</h2>
        <p className="text-muted-foreground text-sm">
          We're performing scheduled maintenance. Please check back shortly.
        </p>
      </div>
    </div>
  );
}

function App() {
  const [showWelcome, setShowWelcome] = useState(false);
  const [showVideoSplash, setShowVideoSplash] = useState(false);
  
  // 🚀 Hide skeleton immediately when React mounts
  useEffect(() => {
    hideLoadingSkeleton();
  }, []);

  // 🕌 ADHAN BOOT INIT (native only, no-op on web).
  // Creates the three per-sound notification channels + reschedules the
  // rolling 7-day window from cached prayer times so notifications survive
  // a device reboot without needing the user to open Settings.
  useEffect(() => {
    bootInitAdhan().then((r) => {
      console.log('[App] Adhan boot init →', r);
    });
  }, []);
  
  // 🎬 Show video splash on FIRST launch only
  useEffect(() => {
    const hasSeenSplash = localStorage.getItem('tanzeel-splash-shown');
    if (!hasSeenSplash) {
      setShowVideoSplash(true);
      localStorage.setItem('tanzeel-splash-shown', 'true');
    }
  }, []);
  
  const handleVideoEnd = () => {
    setShowVideoSplash(false);
  };
  
  // Location setup belongs to prayer setup. It must not block unrelated
  // first-time actions such as reading or sharing a Hadith.
  useEffect(() => {
    const welcomeCompleted = localStorage.getItem('quranplus-welcome-completed');
    const isPrayerSetupRoute = window.location.pathname === '/prayer-times'
      || window.location.pathname === '/prayer-times-original'
      || window.location.pathname === '/prayer-trial';
    if (!welcomeCompleted && isPrayerSetupRoute) {
      // Small delay to let the app render first
      const timer = setTimeout(() => setShowWelcome(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const handleWelcomeComplete = (syncData) => {
    console.log('Welcome completed with sync data:', syncData);
    setShowWelcome(false);
  };

  return (
    <ThemeProvider>
      <SettingsProvider>
        <FortressProvider>
          <ProProvider>
            <PrayerScheduleProvider>
            <ReadingProgressProvider>
            {/* 🎬 Video Splash - Only on first launch */}
            {showVideoSplash && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999
              }}
            >
              <video 
                autoPlay 
                muted 
                playsInline 
                onEnded={handleVideoEnd}
                onError={handleVideoEnd}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              >
                <source src="/splash-video.mp4" type="video/mp4" />
              </video>
              <button
                onClick={handleVideoEnd}
                style={{
                  position: 'absolute',
                  bottom: '40px',
                  right: '24px',
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '10px 24px',
                  borderRadius: '24px',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Skip
              </button>
            </div>
          )}
          <div className="App min-h-screen bg-background">
            <BrowserRouter>
              <AnalyticsTracker />
              <WidgetDeepLinkHandler />
              <FortressBreadcrumbTracker />
              <MaintenanceModeOverlay />
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Standalone Zoom View without Layout */}
                <Route path="quran/zoom/:surahNumber" element={<ZoomViewPage />} />
                
                {/* Standalone Mushaf View (Quran Mode) without Layout */}
                <Route path="quran/mushaf/:surahNumber" element={<MushafViewPage />} />
                
                {/* 🏰 FORTRESS: Admin Dashboard (Standalone - No Layout) */}
                <Route path="admin-fortress" element={<AdminFortressPage />} />
                
                {/* Main App with Layout */}
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="quran" element={<QuranPage />} />
                  <Route path="quran/:surahNumber" element={<SurahPage />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="prayer-times" element={<PrayerTrialPage />} />
                  <Route path="prayer-times-original" element={<PrayerTimes />} />
                  <Route path="hadith" element={<HadithPage />} />
                  <Route path="hadith/:book" element={<HadithPage />} />
                  <Route path="qibla" element={<QiblaPage />} />
                  <Route path="mosques" element={<MasjidGuidePage />} />
                  <Route path="masjid-guide" element={<MasjidGuidePage />} />
                  <Route path="duas" element={<DuasPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="bookmarks" element={<BookmarksPage />} />
                  <Route path="progress" element={<ReadingProgressPage />} />
                  <Route path="halal-scanner" element={<HalalScannerPage />} />
                  <Route path="halal-scanner-2" element={<HalalScanner2Page />} />
                  <Route path="daily-stories" element={<DailyStoriesPage />} />
                  <Route path="learn" element={<LearnPage />} />
                  <Route path="kids-progress" element={<KidsProgressPage />} />
                  <Route path="kids-paradise" element={<KidsParadisePage />} />
                  <Route path="prayer-trial" element={<PrayerTrialPage />} />
                  
                  {/* Web Subscription - Stripe checkout for web users */}
                  <Route path="subscribe" element={<WebSubscriptionPage />} />
                  <Route path="subscription/success" element={<WebSubscriptionPage />} />
                  <Route path="subscription/cancel" element={<WebSubscriptionPage />} />
                  
                  {/* V3 PARALLEL TRACK: Local AI Quran Reader - Pristine inline engine */}
                  <Route path="v3/quran" element={<QuranPageV3 />} />
                  <Route path="v3/quran/:surahNumber" element={<SurahPageV3 />} />
                  
                  {/* Legal Pages */}
                  <Route path="privacy" element={<PrivacyPolicyPage />} />
                  <Route path="delete-account" element={<DeleteAccountPage />} />
                </Route>
            </Routes>
            </Suspense>
          </BrowserRouter>
          
          {/* GPS-Based Welcome Modal for new users - Excluded from admin routes */}
          {showWelcome && !window.location.pathname.includes('admin') && (
            <WelcomeModal 
              isOpen={true} 
              onClose={() => setShowWelcome(false)}
              onComplete={handleWelcomeComplete}
            />
          )}
          
          <Toaster richColors position="top-center" />
        </div>
        </ReadingProgressProvider>
        </PrayerScheduleProvider>
        </ProProvider>
        </FortressProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;
